import { chatAuthHeaders, getChatApiBase } from "@/apiBase";
import { hydrateChatProfileAvatar } from "@/lib/chatAvatar";

/** Plafond pièce jointe Chat (aligné api_chat CHAT_MAX_UPLOAD_BYTES). */
export const CHAT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const CHAT_MAX_UPLOAD_LABEL = "10 Mo";

/** Vidéos Chat (aligné api_chat CHAT_MAX_VIDEO_UPLOAD_BYTES). */
export const CHAT_MAX_VIDEO_UPLOAD_BYTES = 50 * 1024 * 1024;

export const CHAT_MAX_VIDEO_UPLOAD_LABEL = "50 Mo";

export function isChatVideoFile(file: File): boolean {
  const mime = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (name.startsWith("vocal-")) return false;
  if (mime.startsWith("video/") && mime !== "video/ogg") return true;
  return /\.(mp4|mov|m4v|3gp)$/i.test(name);
}

export function chatFileMaxBytes(file: File): number {
  return isChatVideoFile(file) ? CHAT_MAX_VIDEO_UPLOAD_BYTES : CHAT_MAX_UPLOAD_BYTES;
}

export function chatUploadTooLargeMessage(fileName?: string, maxLabel = CHAT_MAX_UPLOAD_LABEL): string {
  const name = fileName?.trim();
  if (name) {
    return `« ${name} » dépasse ${maxLabel}. Choisissez un fichier de ${maxLabel} maximum.`;
  }
  return `Fichier trop volumineux : maximum ${maxLabel} par fichier.`;
}

export type ChatConversationType = "dm" | "group";

export type ChatReceipt = "sent" | "delivered" | "read";

/** Effets d’envoi (appui long sur Envoyer). */
export type ChatMessageEffect = "impact" | "whisper" | "echo";

export type ChatClientRef = {
  id: number;
  name: string;
};

export type ChatColleague = {
  id: number;
  username: string;
  email: string | null;
  presence?: "online" | "away" | "offline" | "dnd" | "meeting" | string;
  /** Unix secondes — dernière activité Chat. */
  last_seen?: number | null;
  avatar_url?: string | null;
  display_name?: string | null;
  client_ids?: number[];
  clients?: ChatClientRef[];
};

export type ChatConversation = {
  id: number;
  type: ChatConversationType;
  title: string | null;
  avatar_url?: string | null;
  member_ids: number[];
  members?: ChatColleague[];
  last_message?: ChatMessage | null;
  unread_count?: number;
  /** Aperçu live (ex. vote sondage) tant que le fil n’est pas ouvert. */
  last_activity_label?: string;
  last_interact?: { kind: string; user_id: number; emoji?: string | null } | null;
  updated_at?: string | null;
  created_by?: number;
  client_id?: number;
  client_name?: string | null;
  /** Salle de visio (groupe ≤ 4). */
  is_room?: boolean;
  /** Salle sans autre membre Suite (invités externes uniquement). */
  guest_only?: boolean;
  /** Visio ringing/active en cours (autres appareils / rejoindre). */
  active_call?: {
    call_id: string;
    room: string;
    media: ChatCallMedia;
    status: "ringing" | "active" | string;
    created_by?: number;
    /** false = visio restreinte, pas dans les participants choisis */
    joinable?: boolean;
  } | null;
};

/** Salle / groupe : pas de sonnerie DM, pas de timer « pas de réponse ». */
export function isOpenVisioConversation(c?: {
  type?: string;
  is_room?: boolean;
  guest_only?: boolean;
} | null): boolean {
  if (!c) return false;
  return c.type === "group" || Boolean(c.is_room) || Boolean(c.guest_only);
}

/** Pastille liste / en-tête : « Invité » si salle sans autre membre Suite. */
export function conversationClientPillName(c: {
  guest_only?: boolean;
  is_room?: boolean;
  member_ids?: number[];
  client_name?: string | null;
}): string | null {
  if (c.guest_only || (Boolean(c.is_room) && (c.member_ids?.length ?? 0) === 1)) {
    return "Invité";
  }
  const name = c.client_name?.trim();
  return name || null;
}

/** false / 0 / "0" = visio restreinte (pas dans les choisis). Absent = ancien appel ouvert. */
export function chatCallIsJoinable(call?: { joinable?: boolean | number | string } | null): boolean {
  if (!call) return false;
  const v = call.joinable;
  if (v === false || v === 0 || v === "0" || v === "false") return false;
  return true;
}

export type ChatAttachment = {
  id?: number;
  url: string;
  name: string;
  mime?: string;
  size?: number;
};

export type ChatReaction = {
  emoji: string;
  count: number;
  user_ids: number[];
};

export type ChatReplyPreview = {
  id: number;
  body: string;
  sender_id: number;
  sender_name?: string | null;
};

export type ChatMessage = {
  id: number;
  conversation_id: number;
  sender_id: number;
  body: string;
  created_at: string;
  updated_at?: string | null;
  reply_to_id?: number | null;
  /** Aperçu du message cité (si chargé avec le fil). */
  reply_to?: ChatReplyPreview | null;
  attachments?: ChatAttachment[];
  sender_name?: string;
  edited?: boolean;
  receipt?: ChatReceipt | null;
  reactions?: ChatReaction[];
  /** impact | echo (whisper = ancien, rendu normal) — null/absent = envoi normal */
  effect?: ChatMessageEffect | null;
  /** Bulle locale, pas encore confirmée par l’API. */
  pending?: boolean;
};

export type ChatSidebarOrderPayload = {
  pinned: number[];
};

export type ChatProfile = {
  user_id: number;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  sidebar_order?: ChatSidebarOrderPayload | null;
  nicknames?: Record<string, string> | null;
};

export type ChatPollEvent =
  | { type: "message"; message: ChatMessage }
  | { type: "typing"; conversation_id: number; user_id: number; typing: boolean }
  | { type: "read"; conversation_id: number; user_id: number; message_id: number }
  | { type: "presence"; user_id: number; presence: string }
  | { type: "message_edited"; message: ChatMessage }
  | { type: "message_deleted"; conversation_id: number; message_id: number }
  | {
      type: "message_reaction";
      conversation_id: number;
      message_id: number;
      user_id: number;
      emoji: string | null;
      action: string;
      reactions: ChatReaction[];
    }
  | { type: "conversation_updated"; conversation: ChatConversation }
  | { type: "conversation_deleted"; conversation_id: number }
  | { type: "conversation_left"; conversation_id: number; user_id?: number }
  | {
      type: "call_invite";
      conversation_id: number;
      call_id: string;
      room: string;
      media: "audio" | "video" | string;
      from_user_id: number;
      from_name?: string;
      member_ids?: number[];
    }
  | { type: "call_accept"; conversation_id: number; call_id: string; user_id: number }
  | { type: "call_reject"; conversation_id: number; call_id: string; user_id: number }
  | { type: "call_end"; conversation_id: number; call_id: string; user_id: number }
  | {
      type: "auriz";
      conversation_id: number;
      from_user_id: number;
      from_name?: string;
      to_user_id: number;
    }
  | { type: string; [key: string]: unknown };

type ApiResult<T> = {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
};

function chatUrl(path: string, query?: Record<string, string | number | undefined>): string | null {
  const base = getChatApiBase();
  if (!base) return null;
  // Base doit être absolue (http…) — un chemin "/api_chat" fait planter new URL() en PWA
  const absoluteBase = /^https?:\/\//i.test(base)
    ? base.replace(/\/?$/, "/")
    : `${typeof window !== "undefined" ? window.location.origin : "http://localhost"}${base.startsWith("/") ? base : `/${base}`}/`;
  const url = new URL(path.replace(/^\//, ""), absoluteBase);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.href;
}

async function chatFetchJson<T>(
  suiteUserId: string | null,
  path: string,
  init: RequestInit = {},
  query?: Record<string, string | number | undefined>
): Promise<ApiResult<T>> {
  const url = chatUrl(path, query);
  if (!url) {
    return { success: false, error: "no_api_url", message: "API chat introuvable." };
  }
  const headers: Record<string, string> = {
    ...chatAuthHeaders(suiteUserId),
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body && !(init.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  try {
    const res = await fetch(url, { credentials: "include", ...init, headers });
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      return { success: false, error: "invalid_json", message: `Réponse non JSON (${res.status}).` };
    }
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { success: true, data: data as T };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

function asArray<T>(raw: unknown): T[] {
  return Array.isArray(raw) ? (raw as T[]) : [];
}

export async function listConversations(
  suiteUserId: string | null
): Promise<ApiResult<ChatConversation[]> & { incallUserIds?: number[] }> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "conversations.php");
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const list = asArray<ChatConversation>(r.data.conversations ?? r.data.items ?? r.data).map((c) => {
    const withCall = c.active_call
      ? { ...c, active_call: { ...c.active_call, joinable: chatCallIsJoinable(c.active_call) } }
      : c;
    return {
      ...withCall,
      avatar_url: hydrateChatProfileAvatar(withCall, suiteUserId).avatar_url,
      members: (withCall.members || []).map((m) => hydrateChatProfileAvatar(m, suiteUserId)),
    };
  });
  const incallUserIds = Array.isArray(r.data.incall_user_ids)
    ? (r.data.incall_user_ids as unknown[])
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];
  return { success: true, data: list, incallUserIds };
}

export async function createConversation(
  suiteUserId: string | null,
  payload: {
    type: ChatConversationType;
    member_ids: number[];
    title?: string;
    is_room?: boolean;
  }
): Promise<ApiResult<ChatConversation>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "conversations.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const conv = (r.data.conversation ?? r.data) as ChatConversation;
  return { success: true, data: conv };
}

export async function listMessages(
  suiteUserId: string | null,
  conversationId: number,
  afterId?: number
): Promise<ApiResult<ChatMessage[]>> {
  const r = await chatFetchJson<Record<string, unknown>>(
    suiteUserId,
    "messages.php",
    { method: "GET" },
    { conversation_id: conversationId, after_id: afterId }
  );
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const list = asArray<ChatMessage>(r.data.messages ?? r.data.items ?? r.data);
  return { success: true, data: list };
}

export async function sendAuriz(
  suiteUserId: string | null,
  conversationId: number
): Promise<ApiResult<{ conversation_id: number; from_user_id: number; from_name?: string; to_user_id: number }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "auriz.php", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const raw = (r.data.auriz ?? r.data) as Record<string, unknown>;
  return {
    success: true,
    data: {
      conversation_id: Number(raw.conversation_id),
      from_user_id: Number(raw.from_user_id),
      from_name: typeof raw.from_name === "string" ? raw.from_name : undefined,
      to_user_id: Number(raw.to_user_id),
    },
  };
}

export async function sendMessage(
  suiteUserId: string | null,
  payload: {
    conversation_id: number;
    body: string;
    reply_to_id?: number;
    effect?: ChatMessageEffect | null;
  }
): Promise<ApiResult<ChatMessage>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "messages.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const msg = (r.data.message ?? r.data) as ChatMessage;
  return { success: true, data: msg };
}

export async function sendMessageWithFiles(
  suiteUserId: string | null,
  payload: {
    conversation_id: number;
    body: string;
    reply_to_id?: number;
    files: File[];
    effect?: ChatMessageEffect | null;
  }
): Promise<ApiResult<ChatMessage>> {
  const url = chatUrl("messages.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };

  const form = new FormData();
  form.append("conversation_id", String(payload.conversation_id));
  form.append("body", payload.body);
  if (payload.reply_to_id != null) form.append("reply_to_id", String(payload.reply_to_id));
  if (payload.effect) form.append("effect", payload.effect);
  for (const file of payload.files) {
    form.append("files[]", file, file.name);
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: chatAuthHeaders(suiteUserId),
      body: form,
    });
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      // Fallback : upload.php puis message texte
      if (payload.files.length > 0) {
        const uploaded = await uploadFiles(suiteUserId, payload.files);
        if (!uploaded.success) {
          return { success: false, error: uploaded.error, message: uploaded.message };
        }
        const attachmentNote =
          uploaded.data && uploaded.data.length > 0
            ? `\n[${uploaded.data.map((a) => a.name).join(", ")}]`
            : "";
        return sendMessage(suiteUserId, {
          conversation_id: payload.conversation_id,
          body: `${payload.body}${attachmentNote}`.trim(),
          reply_to_id: payload.reply_to_id,
          effect: payload.effect,
        });
      }
      return { success: false, error: "invalid_json", message: `Réponse non JSON (${res.status}).` };
    }
    if (!res.ok || data.success === false) {
      if (payload.files.length > 0) {
        const uploaded = await uploadFiles(suiteUserId, payload.files);
        if (!uploaded.success) {
          return {
            success: false,
            error: typeof data.error === "string" ? data.error : uploaded.error,
            message: typeof data.message === "string" ? data.message : uploaded.message,
          };
        }
        const attachmentNote =
          uploaded.data && uploaded.data.length > 0
            ? `\n[${uploaded.data.map((a) => a.name).join(", ")}]`
            : "";
        return sendMessage(suiteUserId, {
          conversation_id: payload.conversation_id,
          body: `${payload.body}${attachmentNote}`.trim(),
          reply_to_id: payload.reply_to_id,
          effect: payload.effect,
        });
      }
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { success: true, data: (data.message ?? data) as ChatMessage };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

export async function uploadFiles(
  suiteUserId: string | null,
  files: File[]
): Promise<ApiResult<ChatAttachment[]>> {
  const url = chatUrl("upload.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };
  const form = new FormData();
  for (const file of files) {
    form.append("files[]", file, file.name);
  }
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: chatAuthHeaders(suiteUserId),
      body: form,
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    const list = asArray<ChatAttachment>(data.attachments ?? data.files ?? data);
    return { success: true, data: list };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

export async function markRead(
  suiteUserId: string | null,
  conversationId: number,
  messageId: number
): Promise<ApiResult<unknown>> {
  return chatFetchJson(suiteUserId, "read.php", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId, message_id: messageId }),
  });
}

export type ChatPresenceStatus = "online" | "dnd" | "meeting" | "offline";

/** Statut affiché (incall = overlay visio live, pas un statut manuel). */
export type ChatPresenceDisplay = ChatPresenceStatus | "incall";

export function normalizeChatPresence(raw?: string | null): ChatPresenceDisplay {
  const s = (raw || "").toLowerCase().trim();
  if (s === "dnd" || s === "busy" || s === "away") return "dnd";
  if (s === "meeting") return "meeting";
  if (s === "incall" || s === "in_call" || s === "visio") return "incall";
  if (s === "online") return "online";
  return "offline";
}

export function isChatInCall(raw?: string | null): boolean {
  return normalizeChatPresence(raw) === "incall";
}

/** DM : le correspondant est en visio si la présence live OU le cache collègues le dit. */
export function isChatPeerBusyInCall(
  peerId: number | null | undefined,
  presence: Record<number, string>,
  extraRaw?: string | null
): boolean {
  const id = Number(peerId);
  if (!Number.isFinite(id) || id < 1) return false;
  return isChatInCall(presence[id]) || isChatInCall(extraRaw);
}

function toManualPresence(raw: ChatPresenceDisplay): ChatPresenceStatus {
  return raw === "incall" ? "online" : raw;
}

/** Change le statut visible (online | dnd | meeting | offline). */
export async function setChatPresenceStatus(
  suiteUserId: string | null,
  status: ChatPresenceStatus
): Promise<ApiResult<{ presence: ChatPresenceStatus }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "presence.php", {
    method: "POST",
    body: JSON.stringify({ status }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: { presence: toManualPresence(normalizeChatPresence(String(r.data.presence ?? r.data.self ?? status))) },
  };
}

/** Lit mon statut courant (+ snapshot collègues). */
export async function fetchChatPresenceSelf(
  suiteUserId: string | null
): Promise<
  ApiResult<{ self: ChatPresenceStatus; presence: Record<string, string>; last_seen: Record<string, number> }>
> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "presence.php", {
    method: "GET",
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const presenceRaw = r.data.presence;
  const presence =
    presenceRaw && typeof presenceRaw === "object" && !Array.isArray(presenceRaw)
      ? (presenceRaw as Record<string, string>)
      : {};
  const lastSeenRaw = r.data.last_seen;
  const last_seen =
    lastSeenRaw && typeof lastSeenRaw === "object" && !Array.isArray(lastSeenRaw)
      ? (lastSeenRaw as Record<string, number>)
      : {};
  return {
    success: true,
    data: {
      self: toManualPresence(normalizeChatPresence(String(r.data.self ?? "offline"))),
      presence,
      last_seen,
    },
  };
}

/** Logout : coupe la pastille tout de suite (updated_at dans le passé). */
export function signalChatPresenceOffline(suiteUserId: string | null): void {
  if (!suiteUserId) return;
  const url = chatUrl("presence.php");
  if (!url) return;
  try {
    void fetch(url, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: {
        ...chatAuthHeaders(suiteUserId),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status: "offline" }),
    }).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

export async function markUnread(
  suiteUserId: string | null,
  conversationId: number
): Promise<ApiResult<{ unread_count?: number; conversation?: ChatConversation }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "read.php", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId, unread: true }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: {
      unread_count: typeof r.data.unread_count === "number" ? r.data.unread_count : undefined,
      conversation: r.data.conversation as ChatConversation | undefined,
    },
  };
}

export async function setTyping(
  suiteUserId: string | null,
  conversationId: number,
  typing: boolean
): Promise<ApiResult<unknown>> {
  return chatFetchJson(suiteUserId, "typing.php", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId, typing }),
  });
}

export async function listColleagues(
  suiteUserId: string | null
): Promise<ApiResult<{ colleagues: ChatColleague[]; clients: ChatClientRef[] }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "colleagues.php");
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const list = asArray<ChatColleague>(r.data.colleagues ?? r.data.users ?? r.data).map((c) =>
    hydrateChatProfileAvatar(c, suiteUserId)
  );
  const clients = asArray<ChatClientRef>(r.data.clients ?? []);
  return { success: true, data: { colleagues: list, clients } };
}

export type ChatCallMedia = "audio" | "video";

export type ChatCallJoinPayload = {
  call_id: string;
  room: string;
  media?: ChatCallMedia;
  livekit_url: string;
  token: string;
  role?: string;
  ring_timeout_sec?: number;
};

export async function chatCallInvite(
  suiteUserId: string | null,
  conversationId: number,
  media: ChatCallMedia = "video",
  inviteeIds?: number[]
): Promise<ApiResult<ChatCallJoinPayload>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({
      action: "invite",
      conversation_id: conversationId,
      media,
      ...(inviteeIds && inviteeIds.length > 0 ? { invitee_ids: inviteeIds } : {}),
    }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const callId = typeof r.data.call_id === "string" ? r.data.call_id : "";
  const room = typeof r.data.room === "string" ? r.data.room : "";
  const livekitUrl = typeof r.data.livekit_url === "string" ? r.data.livekit_url : "";
  const token = typeof r.data.token === "string" ? r.data.token : "";
  if (!callId || !livekitUrl || !token) {
    return { success: false, error: "bad_call_payload", message: "Réponse appel incomplete." };
  }
  return {
    success: true,
    data: {
      call_id: callId,
      room,
      media: r.data.media === "audio" ? "audio" : "video",
      livekit_url: livekitUrl,
      token,
      role: typeof r.data.role === "string" ? r.data.role : "caller",
    },
  };
}

export async function chatCallAccept(
  suiteUserId: string | null,
  conversationId: number,
  callId: string
): Promise<ApiResult<ChatCallJoinPayload>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({ action: "accept", conversation_id: conversationId, call_id: callId }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const livekitUrl = typeof r.data.livekit_url === "string" ? r.data.livekit_url : "";
  const token = typeof r.data.token === "string" ? r.data.token : "";
  const room = typeof r.data.room === "string" ? r.data.room : "";
  if (!livekitUrl || !token) {
    return { success: false, error: "bad_call_payload", message: "Réponse appel incomplete." };
  }
  return {
    success: true,
    data: {
      call_id: callId,
      room,
      livekit_url: livekitUrl,
      token,
      role: "callee",
    },
  };
}

/** Rejoindre une visio en cours (membre de la salle) ou basculer depuis un autre appareil. */
export async function chatCallJoin(
  suiteUserId: string | null,
  conversationId: number,
  callId?: string
): Promise<ApiResult<ChatCallJoinPayload & { handoff?: boolean }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({
      action: "join",
      conversation_id: conversationId,
      ...(callId ? { call_id: callId } : {}),
    }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const joinedCallId = typeof r.data.call_id === "string" ? r.data.call_id : callId || "";
  const room = typeof r.data.room === "string" ? r.data.room : "";
  const livekitUrl = typeof r.data.livekit_url === "string" ? r.data.livekit_url : "";
  const token = typeof r.data.token === "string" ? r.data.token : "";
  if (!joinedCallId || !livekitUrl || !token) {
    return { success: false, error: "bad_call_payload", message: "Réponse appel incomplete." };
  }
  return {
    success: true,
    data: {
      call_id: joinedCallId,
      room,
      media: r.data.media === "audio" ? "audio" : "video",
      livekit_url: livekitUrl,
      token,
      role: typeof r.data.role === "string" ? r.data.role : "member",
      handoff: r.data.handoff === true,
    },
  };
}

export async function chatCallActive(
  suiteUserId: string | null,
  conversationId: number
): Promise<
  ApiResult<{
    call: {
      call_id: string;
      room: string;
      media: ChatCallMedia;
      status: string;
      created_by?: number;
      joinable?: boolean;
      participant_count?: number;
    } | null;
  }>
> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({ action: "active", conversation_id: conversationId }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const raw = r.data.call;
  if (!raw || typeof raw !== "object") {
    return { success: true, data: { call: null } };
  }
  const c = raw as Record<string, unknown>;
  const callId = typeof c.call_id === "string" ? c.call_id : "";
  if (!callId) return { success: true, data: { call: null } };
  return {
    success: true,
    data: {
      call: {
        call_id: callId,
        room: typeof c.room === "string" ? c.room : "",
        media: c.media === "audio" ? "audio" : "video",
        status: typeof c.status === "string" ? c.status : "active",
        created_by: typeof c.created_by === "number" ? c.created_by : undefined,
        joinable: chatCallIsJoinable({ joinable: c.joinable as boolean | number | string | undefined }),
        participant_count:
          typeof c.participant_count === "number" ? c.participant_count : undefined,
      },
    },
  };
}

export async function chatCallReject(
  suiteUserId: string | null,
  conversationId: number,
  callId: string
): Promise<ApiResult<{ call_id: string }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({ action: "reject", conversation_id: conversationId, call_id: callId }),
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return { success: true, data: { call_id: callId } };
}

export async function chatCallHangup(
  suiteUserId: string | null,
  conversationId: number,
  callId: string
): Promise<ApiResult<{ call_id: string }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({ action: "hangup", conversation_id: conversationId, call_id: callId }),
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return { success: true, data: { call_id: callId } };
}

/** Keepalive participant pendant la visio (anti-fantômes). */
export async function chatCallTouch(
  suiteUserId: string | null,
  conversationId: number,
  callId: string
): Promise<ApiResult<{ call_id: string }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({ action: "touch", conversation_id: conversationId, call_id: callId }),
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return { success: true, data: { call_id: callId } };
}

export async function chatCallAddParticipant(
  suiteUserId: string | null,
  conversationId: number,
  callId: string,
  userId: number
): Promise<ApiResult<{ call_id: string; user_id: number; active_count?: number }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({
      action: "add_participant",
      conversation_id: conversationId,
      call_id: callId,
      user_id: userId,
    }),
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: {
      call_id: callId,
      user_id: userId,
      active_count:
        typeof r.data?.active_count === "number" ? r.data.active_count : undefined,
    },
  };
}

export async function chatCallMiss(
  suiteUserId: string | null,
  conversationId: number,
  callId: string,
  media: ChatCallMedia = "video"
): Promise<ApiResult<{ call_id: string }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "call.php", {
    method: "POST",
    body: JSON.stringify({
      action: "miss",
      conversation_id: conversationId,
      call_id: callId,
      media,
    }),
  });
  if (!r.success) return { success: false, error: r.error, message: r.message };
  return { success: true, data: { call_id: callId } };
}

/** Ticket court pour ouvrir le WebSocket (même auth session / X-User-Id). */
export async function fetchChatWsTicket(
  suiteUserId: string | null
): Promise<ApiResult<{ ticket: string; expires_in: number; ws_path: string }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "ws-ticket.php");
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const ticket = typeof r.data.ticket === "string" ? r.data.ticket : "";
  const wsPath = typeof r.data.ws_path === "string" ? r.data.ws_path : "/api_chat/ws";
  const expiresIn = typeof r.data.expires_in === "number" ? r.data.expires_in : 120;
  if (!ticket) {
    return { success: false, error: "no_ticket", message: "Ticket WebSocket manquant." };
  }
  return { success: true, data: { ticket, expires_in: expiresIn, ws_path: wsPath } };
}

/** URL wss://…/api_chat/ws?ticket=… (same-origin sur chat.app). */
export function buildChatWsUrl(ticket: string, wsPath = "/api_chat/ws"): string | null {
  if (typeof window === "undefined" || !ticket) return null;
  const base = getChatApiBase();
  let path = wsPath.startsWith("/") ? wsPath : `/${wsPath}`;
  if (base) {
    try {
      const u = new URL(base);
      // base = …/api_chat → WS sur …/api_chat/ws
      path = `${u.pathname.replace(/\/$/, "")}/ws`;
      const proto = u.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${u.host}${path}?ticket=${encodeURIComponent(ticket)}`;
    } catch {
      /* fall through */
    }
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${path}?ticket=${encodeURIComponent(ticket)}`;
}

export async function pollChatEvents(
  suiteUserId: string | null,
  opts: {
    since?: string;
    since_id?: number;
    bootstrap?: boolean;
    signal?: AbortSignal;
  } = {}
): Promise<
  ApiResult<{
    events: ChatPollEvent[];
    since?: string;
    since_id?: number;
    presence?: Record<string, string>;
    last_seen?: Record<string, number>;
  }>
> {
  const url = chatUrl("poll.php", {
    since: opts.bootstrap ? undefined : opts.since,
    since_id: opts.bootstrap ? undefined : opts.since_id,
    bootstrap: opts.bootstrap ? 1 : undefined,
  });
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };
  try {
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: chatAuthHeaders(suiteUserId),
      signal: opts.signal,
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    const events = asArray<ChatPollEvent>(data.events ?? data.items ?? []);
    const presenceRaw = data.presence;
    const presence =
      presenceRaw && typeof presenceRaw === "object" && !Array.isArray(presenceRaw)
        ? (presenceRaw as Record<string, string>)
        : undefined;
    const lastSeenRaw = data.last_seen;
    const last_seen =
      lastSeenRaw && typeof lastSeenRaw === "object" && !Array.isArray(lastSeenRaw)
        ? (lastSeenRaw as Record<string, number>)
        : undefined;
    return {
      success: true,
      data: {
        events,
        since: typeof data.since === "string" ? data.since : undefined,
        since_id: typeof data.since_id === "number" ? data.since_id : undefined,
        presence,
        last_seen,
      },
    };
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      return { success: false, error: "aborted", message: "Annulé" };
    }
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

export async function renameConversation(
  suiteUserId: string | null,
  id: number,
  title: string
): Promise<ApiResult<ChatConversation>> {
  return updateConversation(suiteUserId, { id, title });
}

export async function updateConversation(
  suiteUserId: string | null,
  opts: { id: number; title?: string; avatar?: File }
): Promise<ApiResult<ChatConversation>> {
  const url = chatUrl("conversations.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };

  // Multipart si avatar ; sinon PATCH JSON (titre seul)
  if (opts.avatar) {
    const form = new FormData();
    form.append("action", "update");
    form.append("id", String(opts.id));
    if (opts.title !== undefined) form.append("title", opts.title);
    form.append("avatar", opts.avatar, opts.avatar.name);
    try {
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: chatAuthHeaders(suiteUserId),
        body: form,
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok || data.success === false) {
        return {
          success: false,
          error: typeof data.error === "string" ? data.error : `http_${res.status}`,
          message: typeof data.message === "string" ? data.message : res.statusText,
        };
      }
      return { success: true, data: (data.conversation ?? data) as ChatConversation };
    } catch (e) {
      return {
        success: false,
        error: "network",
        message: e instanceof Error ? e.message : "Erreur réseau",
      };
    }
  }

  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "conversations.php", {
    method: "PATCH",
    body: JSON.stringify({ id: opts.id, title: opts.title }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: (r.data.conversation ?? r.data) as ChatConversation };
}

export async function removeConversationMember(
  suiteUserId: string | null,
  conversationId: number,
  memberId: number
): Promise<ApiResult<ChatConversation>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "conversations.php", {
    method: "PATCH",
    body: JSON.stringify({ id: conversationId, remove_member_id: memberId }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: (r.data.conversation ?? r.data) as ChatConversation };
}

export async function addConversationMember(
  suiteUserId: string | null,
  conversationId: number,
  memberId: number
): Promise<ApiResult<ChatConversation>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "conversations.php", {
    method: "PATCH",
    body: JSON.stringify({ id: conversationId, add_member_id: memberId }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: (r.data.conversation ?? r.data) as ChatConversation };
}

export async function deleteConversation(
  suiteUserId: string | null,
  id: number,
  mode: "leave" | "delete" = "leave"
): Promise<ApiResult<unknown>> {
  return chatFetchJson(suiteUserId, "conversations.php", {
    method: "DELETE",
    body: JSON.stringify({ id, mode }),
  });
}

export async function editMessage(
  suiteUserId: string | null,
  id: number,
  body: string
): Promise<ApiResult<ChatMessage>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "messages.php", {
    method: "PUT",
    body: JSON.stringify({ id, body }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: (r.data.message ?? r.data) as ChatMessage };
}

export async function deleteMessage(
  suiteUserId: string | null,
  id: number
): Promise<ApiResult<unknown>> {
  return chatFetchJson(suiteUserId, "messages.php", {
    method: "DELETE",
    body: JSON.stringify({ id }),
  });
}

export async function reactToMessage(
  suiteUserId: string | null,
  messageId: number,
  emoji: string
): Promise<ApiResult<{ message_id: number; conversation_id: number; reactions: ChatReaction[] }>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "reactions.php", {
    method: "POST",
    body: JSON.stringify({ message_id: messageId, emoji }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return {
    success: true,
    data: {
      message_id: Number(r.data.message_id ?? messageId),
      conversation_id: Number(r.data.conversation_id ?? 0),
      reactions: asArray<ChatReaction>(r.data.reactions ?? []),
    },
  };
}

export async function fetchChatProfile(
  suiteUserId: string | null,
  userId?: number
): Promise<ApiResult<ChatProfile>> {
  const r = await chatFetchJson<Record<string, unknown>>(
    suiteUserId,
    "profile.php",
    { method: "GET" },
    userId != null ? { user_id: userId } : undefined
  );
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: hydrateChatProfileAvatar((r.data.profile ?? r.data) as ChatProfile, suiteUserId) };
}

export async function saveChatNicknames(
  suiteUserId: string | null,
  nicknames: Record<number, string>
): Promise<ApiResult<ChatProfile>> {
  const payload: Record<string, string> = {};
  for (const [k, v] of Object.entries(nicknames)) {
    const id = Number(k);
    const name = String(v ?? "").trim();
    if (!Number.isFinite(id) || id < 1 || !name) continue;
    payload[String(id)] = name.slice(0, 40);
  }
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "profile.php", {
    method: "POST",
    body: JSON.stringify({ nicknames: payload }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: hydrateChatProfileAvatar((r.data.profile ?? r.data) as ChatProfile, suiteUserId) };
}

export async function saveChatSidebarOrder(
  suiteUserId: string | null,
  order: ChatSidebarOrderPayload
): Promise<ApiResult<ChatProfile>> {
  const r = await chatFetchJson<Record<string, unknown>>(suiteUserId, "profile.php", {
    method: "POST",
    body: JSON.stringify({ sidebar_order: order }),
  });
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  return { success: true, data: hydrateChatProfileAvatar((r.data.profile ?? r.data) as ChatProfile, suiteUserId) };
}

export async function updateChatProfile(
  suiteUserId: string | null,
  opts: { display_name?: string; avatar?: File | null }
): Promise<ApiResult<ChatProfile>> {
  const url = chatUrl("profile.php");
  if (!url) return { success: false, error: "no_api_url", message: "API chat introuvable." };

  const form = new FormData();
  if (opts.display_name !== undefined) form.append("display_name", opts.display_name);
  if (opts.avatar) form.append("avatar", opts.avatar, opts.avatar.name);

  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: chatAuthHeaders(suiteUserId),
      body: form,
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return { success: true, data: hydrateChatProfileAvatar((data.profile ?? data) as ChatProfile, suiteUserId) };
  } catch (e) {
    return {
      success: false,
      error: "network",
      message: e instanceof Error ? e.message : "Erreur réseau",
    };
  }
}

export type ChatGifSearchItem = {
  id: string;
  title: string;
  preview: string;
  url: string;
};

/** Recherche GIF via api_chat/gifs.php (clé Giphy côté serveur). */
export async function searchChatGifs(
  suiteUserId: string | null,
  query?: string
): Promise<ApiResult<{ configured: boolean; gifs: ChatGifSearchItem[] }>> {
  const r = await chatFetchJson<Record<string, unknown>>(
    suiteUserId,
    "gifs.php",
    { method: "GET" },
    query && query.trim() ? { q: query.trim() } : undefined
  );
  if (!r.success || !r.data) return { success: false, error: r.error, message: r.message };
  const gifs = asArray<ChatGifSearchItem>(r.data.gifs ?? []);
  return {
    success: true,
    data: {
      configured: r.data.configured !== false,
      gifs,
    },
  };
}
